import React, { useState } from 'react';
import BabyFeet from '../assets/babyfeet.png';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import { IPassword } from '../types/get-started.type';
import { formatToMoney, passwordStrengthVariables } from 'utils';
import { useGetAPlan } from 'common/queries-and-mutations/payment';
import { Loader } from 'components/Loader';

type Props = {
  onSubmit: (args: IPassword) => void;
  planId: number | null;
};

export default function SetupPassword({ onSubmit, planId }: Props) {
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: selectedPlan, isFetching } = useGetAPlan({
    planId: planId!
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required('Password is required')
        .matches(
          // eslint-disable-next-line no-useless-escape
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()_+{}\[\]:;<>,.?~|]).{8,40}$/,
          'Password must have at least one uppercase, one lowercase, one number, one character and be at least 8 characters long'
        )
        .nullable(),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required')
        .nullable()
    }),
    onSubmit: (values, { setFieldTouched }) => {
      setIsProcessing(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSubmit(values);
    }
  });

  if (isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div className="flex flex-col-reverse md:flex-row">
      <div className="mx-9 my-20 md:mx-20 md:mt-44 md:w-1/2">
        <p className="text-3xl font-medium text-[#353535]">
          Let’s Create Your Account
        </p>
        <p className="text-sm font-bold text-primary-900">
          Selected Plan: {selectedPlan?.planType}{' '}
          {formatToMoney(selectedPlan?.price)}
        </p>

        <form
          autoComplete="false"
          className="mt-10 flex flex-col gap-10"
          onSubmit={formik.handleSubmit}
        >
          <TextField
            type="password"
            name="password"
            placeholder=""
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Password"
            error={formik.touched.password ? formik.errors.password : ''}
            isRequired
            size="lg"
            className="rounded-2xl border-[1.5px] border-gray-300"
            hasStrengthIndicator={
              passwordStrengthVariables(formik.values).hasStrengthIndicator
            }
            totalStrengthConditions={
              passwordStrengthVariables(formik.values).totalStrengthConditions
            }
            strengthConditionsFulfilled={
              passwordStrengthVariables(formik.values)
                .strengthConditionsFulfilled
            }
          />
          <TextField
            type="password"
            name="confirmPassword"
            placeholder=""
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Confirm Password"
            error={
              formik.touched.confirmPassword
                ? formik.errors.confirmPassword
                : ''
            }
            isRequired
            size="lg"
            className="rounded-2xl border-[1.5px] border-gray-300"
          />
          <Button
            variant="primary"
            size="lg"
            className="mt-5 w-[7rem] rounded p-[0.1rem]"
            label="Proceed"
            type="submit"
            disabled={!formik.isValid || isProcessing}
            loading={isProcessing}
          ></Button>
        </form>
      </div>
      <div className="md:w-1/2">
        <img
          src={BabyFeet}
          className="hidden h-auto w-full object-cover md:flex md:h-screen md:flex-col"
        />
      </div>
    </div>
  );
}
